<template>
  <div
    v-if="initialized"
    :class="{
      'environment-staging-border': environment === VITE_ENV_STAGING,
      'environment-development-border': environment === VITE_ENV_DEVELOPMENT,
      'environment-review-border': environment === VITE_ENV_REVIEW,
    }"
    class="h-full w-full"
  >
    <AccessRevokedPage v-if="block_emp" />
    <div
      v-else
      class="h-full w-full"
    >
      <Banner />
      <router-view />
    </div>
  </div>
  <div v-else-if="$route.name === ROUTE_COMMON.LOGIN || $route.name === ROUTE_COMMON.NOTIFICATIONS_REDIRECT">
    <router-view />
  </div>
  <b-loading
    v-else
    :model-value="true"
  />
  <Toast />
  <GlobalConfirmModal />
</template>

<script>
/* oxlint-disable max-lines */
import Banner from '@/app/shared_components/Banner.vue';
import AccessRevokedPage from '@/app/_employees/pages/AccessRevokedPage.vue';
import swal from 'sweetalert';
import {
  PROFILE_TYPES,
  Tenant,
  TENANT_PICTURE_FAVICON_URL,
  TENANT_PRIMARY_COLOR_DEFAULT,
} from '@/app/data/model_constants';
import {
  PROFILE_TO_ROUTES,
  PROFILE_TO_START_ROUTE,
  ROUTE_COMMON,
  ROUTES_PARENT_DOMAIN_REGEX,
  ROUTES_USERS,
} from '@/app/data/route_constants';
import * as Sentry from '@sentry/vue';
import {
  LOGIN_PAGE_NOTIFICATIONS,
  VITE_ENV_DEVELOPMENT,
  VITE_ENV_PRODUCTION,
  VITE_ENV_REVIEW,
} from '@/app/data/general_constants';
import { mapActions, mapGetters } from 'vuex';
import { ZIGGU_PARENT_URL, zigguProfileSubdomain } from '@/app/util/urls';
import { useMqService } from '@/plugins/mq';
import { getCurrentInstance } from 'vue';
import profile_mixin from '@/app/util/profile_mixin';
import useImpersonation from '@/app/util/impersonation';
import useZoomPrevention from '@/app/composables/useZoomPrevention';
import { useI18n } from 'vue-i18n';
import { applyTheme } from '@/app/util/theme_functions';
import { runTicketDraftSignatureMigration } from '@/app/util/migrations/ticket_draft_signature_migration';
import runFilterUndefinedMigration from '@/app/util/migrations/filter_undefined_migration';
import { useToasts } from '@/app/util/toasts';

export default {
  name: 'App',

  components: {
    AccessRevokedPage,
    Banner,
  },
  mixins: [profile_mixin],

  setup() {
    const mq = useMqService();
    const { t } = useI18n();
    const { stopImpersonation, impersonationURL, supervisorURL } = useImpersonation();
    const { disableZoom, cleanupZoomPrevention } = useZoomPrevention();
    const { toast_action_failed } = useToasts();

    return {
      mq,
      t,
      stopImpersonation,
      impersonationURL,
      supervisorURL,
      disableZoom,
      cleanupZoomPrevention,
      toast_action_failed,
    };
  },

  data() {
    return {
      initialized: false,
      ROUTE_COMMON,
    };
  },

  computed: {
    ...mapGetters([
      'billings',
      'current_is_impersonation',
      'current_profile',
      'current_profile_true',
      'current_client',
      'current_tenant',
      'current_user',
    ]),

    authenticated() {
      return this.$auth.is_authenticated;
    },
    is_parent_domain() {
      return ZIGGU_PARENT_URL === window.location.origin;
    },

    billing_client() {
      if (!this.current_client) return {};

      return _.find(this.billings, ['attributes.client_id', this.current_client.id]);
    },

    block_emp() {
      return (
        _.get(this.current_profile, 'type') === PROFILE_TYPES.EMPLOYEE &&
        _.get(this.current_client, 'attributes.revoke_access_emp')
      );
    },
  },

  watch: {
    authenticated: {
      immediate: true,
      async handler(value) {
        if (!value) return;
        // notification redirect page skips regular init flow (uses URL nav <-> of router nav: forces re-init)
        if (this.window.location.pathname.startsWith('/notifications')) return;
        await this.initUser();
      },
    },

    $route: {
      immediate: true,
      handler() {
        if (this.initialized) this.initTitle();
      },
    },
  },

  created() {
    runFilterUndefinedMigration();

    // used only once to get the buefy modal context issue working.
    // would not recommend to use this another time
    const app = getCurrentInstance();
    app.appContext.app.config.globalProperties.mq = this.mq;

    // general handler (also used by login page)
    this.emitter.$on('initFavicon', this.initFavicon);
    this.emitter.$on('initTheme', this.initTheme);

    // user handler
    this.emitter.$on('initUser', this.initUser);

    // profile handlers
    this.emitter.$on('initIntercom', this.initIntercom);
    this.emitter.$on('initTitle', this.initTitle);
    this.emitter.$on('logout', this.logout);
    this.emitter.$on('initPWA', this.initPWA);

    // add impersonation meta attributes in sessionStorage, if present in URL
    const params = new URL(document.location).searchParams;
    const profile_type = params.get('_profile_type');
    const profile_id = params.get('_profile_id');
    const url_return_to = params.get('_return_to');
    if (profile_type && profile_id) {
      sessionStorage.setItem('_profile_type', profile_type);
      sessionStorage.setItem('_profile_id', profile_id);
    }
    if (url_return_to) {
      sessionStorage.setItem('_impersonation_return_to', url_return_to);

      const url = new URL(window.location.href);
      url.searchParams.delete('_return_to');
      window.history.replaceState({}, document.title, url.toString());
    }

    // api error handlers (for all routes except /login, ignored via api/index.js, login page handles it separately)
    this.emitter.$on('errors:401', this.handleErrorUnauthorized);
    this.emitter.$on('errors:403', this.handleErrorForbidden);
    this.emitter.$on('errors:404', this.handleErrorNotFound);
    this.emitter.$on('errors:422', this.handleErrorUnprocessable);
    this.emitter.$on('errors:500', this.handleErrorInternalServer);
    this.emitter.$on('errors:418', this.handleErrorForbiddenImpersonation);
  },

  mounted() {
    // Apply zoom prevention to the entire app
    this.disableZoom();
  },

  beforeUnmount() {
    // Clean up zoom prevention
    this.cleanupZoomPrevention(document.body);
  },

  methods: {
    ...mapActions(['UPDATE_EMAIL_ADDRESS_RESET', 'FETCH_FEATURE_TOGGLES', 'FETCH_USER_PROFILE', 'LOGOUT_USER']),

    initTheme() {
      const theme_primary_color =
        _.get(this.current_profile, 'type') === PROFILE_TYPES.CUSTOMER
          ? this.current_tenant.attributes.theme_primary_color
          : TENANT_PRIMARY_COLOR_DEFAULT;
      applyTheme(theme_primary_color);
    },

    async logout() {
      const impersonation =
        sessionStorage.getItem('_profile_type') !== null && sessionStorage.getItem('_profile_id') !== null;
      // logout trigger during impersonation stops impersonation
      if (impersonation) {
        await this.stopImpersonation();
      } else {
        // retrieve and append any (present) ziggu parameters to pass onto login page
        let subdomain;
        // both no profile (TLD) and partner profile redirect back to TLD
        if (!this.current_profile || this.current_profile.type === PROFILE_TYPES.PARTNER) {
          subdomain = null;
          // other profiles redirect back to subdomain URL
        } else {
          subdomain = zigguProfileSubdomain(this.current_profile, this.current_tenant);
        }
        const email = _.get(this.current_user, 'attributes.email_address', '');
        const notification = LOGIN_PAGE_NOTIFICATIONS.SUCCESS_LOGOUT;
        window.Intercom('shutdown');
        await this.LOGOUT_USER({});
        const params = [];
        if (subdomain) params.push(`subdomain=${subdomain}`);
        if (email) params.push(`email=${email}`);
        if (notification) params.push(`notification=${notification}`);
        let url = `${ZIGGU_PARENT_URL}/login`;
        if (params.length > 0) url += `?${params.join('&')}`;
        window.location.href = url;
      }
    },

    async handleErrorUnauthorized() {
      // illegal route navigation to other profile type routes
      if (
        this.current_profile &&
        this.$route &&
        this.$route.name &&
        !this.$route.name.startsWith(this.current_profile.type.toLowerCase())
      ) {
        await this.initUser();
        // unauthorized error after init: profile does not exist for given tenant or is not invited/active
        //  redirect back to parent domain name
      } else {
        // remove possible impersonation meta params, to prevent endless loop
        sessionStorage.removeItem('_profile_id');
        sessionStorage.removeItem('_profile_type');
        // parent domain on user namespace: no profile exists for this user, force logout
        if (
          this.is_parent_domain &&
          !window.location.pathname.startsWith('/employee') &&
          !window.location.pathname.startsWith('/partner')
        ) {
          // retrieve and append any (present) ziggu parameters to pass onto login page
          const email = _.get(this.current_user, 'attributes.email_address', '');
          const notification = LOGIN_PAGE_NOTIFICATIONS.ERROR_NO_ACCESS;
          await this.LOGOUT_USER({});
          const params = [];
          if (email) params.push(`email=${email}`);
          if (notification) params.push(`notification=${notification}`);
          let url = `${ZIGGU_PARENT_URL}/login`;
          if (params.length > 0) url += `?${params.join('&')}`;
          window.location.href = url;
          // subdomain: no profile for user on this subdomain, redirect to parent domain
        } else {
          const wrapper = document.createElement('div');
          wrapper.innerHTML = this.$t('session.noaccess.text');
          await swal({
            title: this.$t('session.noaccess.title'),
            content: wrapper,
          });
          window.location.href = ZIGGU_PARENT_URL;
        }
      }
    },

    handleErrorForbidden() {
      this.toast_action_failed();
    },

    handleErrorForbiddenImpersonation() {
      const message = this.$t('toast.action.impersonate_unauthorized');
      this.toast_action_failed({ message });
    },

    handleErrorInternalServer() {
      this.toast_action_failed();
    },

    handleErrorNotFound() {
      // app needs to be initialized to render 404-page
      if (this.initialized) {
        this.$router.push({ name: ROUTE_COMMON.ERROR_NOT_FOUND });
        // app not initialized (FETCH_USER_PROFILE call likely gave a 404), redirect back to root of TLD
      } else {
        // remove possible impersonation meta params, to prevent endless loop
        sessionStorage.removeItem('_profile_id');
        sessionStorage.removeItem('_profile_type');
        window.location.href = window.location.origin;
      }
    },

    handleErrorUnprocessable(error) {
      const message = this.$t('toast.action.unprocessable', { error });
      this.toast_action_failed({ message });
    },

    async initUser() {
      this.initialized = false;
      await this.FETCH_USER_PROFILE({});
      this.initTheme();
      // parent domain in user namespace
      if (this.is_parent_domain && !ROUTES_PARENT_DOMAIN_REGEX.test(window.location.pathname)) {
        this.initialized = true;
        this.$router.push({ name: ROUTES_USERS.DASHBOARD });
        // employee/partner namespace via subdomain => parent domain redirect, keep path (no longer supported since PWA)
      } else if (!this.is_parent_domain && ROUTES_PARENT_DOMAIN_REGEX.test(window.location.pathname)) {
        const url_redirect = `${ZIGGU_PARENT_URL}${window.location.pathname}${window.location.search}`;
        window.location.href = url_redirect;
        // customer namespace (subdomain)
      } else {
        await this.FETCH_FEATURE_TOGGLES({});
        this.initSentry();
        await this.initStore();

        // TODO: Remove after 2025-02-20 when all users have logged in at least once
        try {
          this.runTicketDraftMigration();
        } catch (error) {
          try {
            Sentry.captureException(error);
          } catch (e) {
            // ignore
          }
        }

        const { language } = this.current_profile.attributes;
        this.$i18n.locale = language;
        this.initialized = true;

        const { email_address_reset_token } = this.$route.query;
        if (email_address_reset_token) this.confirmEmailChange(email_address_reset_token);

        const profile_type = this.current_profile.type;
        const profile_routes = PROFILE_TO_ROUTES[profile_type];
        if (
          this.$route.name &&
          (!profile_routes.includes(this.$route.name) || this.$route.name.toLowerCase().includes('root')) &&
          ROUTE_COMMON.NOTIFICATIONS_REDIRECT !== this.$route.name
        ) {
          const route_start = PROFILE_TO_START_ROUTE[profile_type];
          let params = {};
          if (profile_type !== PROFILE_TYPES.SUPERVISOR) {
            const subdomain = this.current_tenant.attributes.subdomain;
            params = { subdomain };
            if (
              profile_type === PROFILE_TYPES.CUSTOMER ||
              this.current_tenant.attributes.status === Tenant.STATUS_TEST
            ) {
              // customers access platform via client subdomain
              await this.$router.push({ name: route_start, params });
            } else if (window.location.origin === ZIGGU_PARENT_URL) {
              await this.$router.push({ name: route_start, params });
            } else {
              // employees & partners: enforce access via top level domain (consistency with PWA)
              const url_path = this.$router.resolve({ name: route_start, params }).href;
              window.location.href = `${ZIGGU_PARENT_URL}${url_path}`;
            }
          } else {
            await this.$router.push({ name: route_start, params });
          }
        }
      }
    },

    async confirmEmailChange(email_address_reset_token) {
      if (!email_address_reset_token) return;
      try {
        await this.UPDATE_EMAIL_ADDRESS_RESET({ email_address_reset_token });
        this.emitter.$emit('banner', { message: this.$t('profile.labels.updateEmailSuccessful'), color: 'success' });
        const query = { ...this.$route.query };
        delete query.email_address_reset_token;
        this.$router.replace(this.$route.path, { query });
      } catch {
        this.emitter.$emit('banner', { message: this.$t('profile.labels.updateEmailFailed'), color: 'danger' });
        throw new Error('user failed to update email');
      }
    },

    initIntercom() {
      // disable for test clients and for actual customers
      if (
        _.get(this.current_client, 'attributes.status') === Tenant.STATUS_TEST ||
        this.current_profile_true.type === PROFILE_TYPES.CUSTOMER
      )
        return;

      // see https://developers.intercom.com/installing-intercom/docs/javascript-api-attributes-objects
      let options = {};
      // development environment fallbacks to dummy 'Demo User', regardless of profile
      if (import.meta.env.VITE_ENV === VITE_ENV_DEVELOPMENT) {
        options = {
          user_id: 'demo_user_id',
          user_hash: '97a94ce0babc3f5e78aa530c9f481b54b07dd658da28be58560a7218bfcd1f87',
          name: 'Demo User',
        };
      } else {
        let impersonation_url;
        let supervisor_url;
        try {
          impersonation_url = this.impersonationURL(this.current_profile_true, this.current_client);
          supervisor_url = this.supervisorURL(this.current_profile_true);
        } catch (error) {
          impersonation_url = '';
          supervisor_url = '';
        }
        options = {
          impersonation_url,
          supervisor_url,
          intercom_user_jwt: this.current_user.attributes.intercom_user_jwt,
          user_id: this.current_user.attributes.intercom_user_id,
          name: this.current_profile_true.attributes.name,
          email: this.current_profile_true.attributes.email_address,
          first_name: this.current_profile_true.attributes.first_name,
          last_name: this.current_profile_true.attributes.last_name,
          language: this.current_profile_true.attributes.language,
          package: _.get(this.current_client, 'attributes.package'),
          phone_mobile: _.get(this.current_profile_true, 'attributes.phone_mobile'),
          phone_landline: _.get(this.current_profile_true, 'attributes.phone_landline'),
          profile_type: this.current_profile_true.type,
          client_id: _.get(this.current_client, 'id'),
          subdomain: _.get(this.current_profile, 'attributes.subdomain'),
          arr: _.get(this.billing_client, 'attributes.arr'),
          avatar: { type: 'avatar', image_url: this.current_profile_true.attributes.avatar_url },
          profiles_count: this.current_profile_true.attributes.profiles_count,
        };
        // add company data for employee and partner profiles
        if (this.current_profile_true.type === PROFILE_TYPES.EMPLOYEE) {
          // employee profile's 'company' represents client's specifics
          options.company = {
            company_id: this.current_client.attributes.subdomain,
            company_status: this.current_client.attributes.status,
            created_at: Math.floor(Date.parse(this.current_client.attributes.created_at) / 1000),
            name: this.current_client.attributes.name,
          };
          options.role = this.current_profile_true.attributes.role;
          options.profession = this.current_profile_true.attributes.profession;
        } else if (this.current_profile_true.type === PROFILE_TYPES.PARTNER) {
          // partner profile's 'company' represents actual partner's company (with email address domain as unique id)
          const company_id = this.current_profile_true.attributes.email_address.split('@')[1];
          options.company = {
            company_id,
            company_status: this.current_client.attributes.status,
            created_at: Math.floor(Date.parse(this.current_client.attributes.created_at) / 1000),
            name: company_id,
          };
        }
      }
      // hide launcher when impersonating customers to avoid any confusion
      if (this.current_profile.type === PROFILE_TYPES.CUSTOMER) {
        options.hide_default_launcher = true;
      }
      options.ft_mobile_app_customer = this.$flipper.enabled(this.$FT.MOBILE_APP_CUSTOMER);
      window.Intercom('boot', {
        app_id: 'kqlc8dey',
        hide_default_launcher: true,
        ...options,
      });
    },

    initSentry() {
      const scope = Sentry.getCurrentScope();
      scope.setUser({
        id: this.current_profile_true.attributes.user_id,
        profile_name: this.current_profile.attributes.name,
        profile_true_name: this.current_profile_true.attributes.name,
      });
      const { client_id } = this.current_profile.attributes;
      if (client_id) scope.setTag('client_id', client_id);
      scope.setTag('profile_tid', `${this.current_profile.type}#${this.current_profile.id}`);
      scope.setTag('profile_true_tid', `${this.current_profile_true.type}#${this.current_profile_true.id}`);
      if (import.meta.env.VITE_ENV === VITE_ENV_REVIEW) {
        scope.setTag('pull_request_id', _.parseInt(import.meta.env.VITE_PULL_REQUEST_ID));
      }
    },

    async initStore() {
      // dynamic import of static pathnames
      if (!this.current_profile) return;
      const profile_store = await (() => {
        switch (this.current_profile.type) {
          case PROFILE_TYPES.CUSTOMER:
            return import('@/store/customers/index.js');
          case PROFILE_TYPES.EMPLOYEE:
            return import('@/store/employees/index.js');
          case PROFILE_TYPES.PARTNER:
            return import('@/store/partners/index.js');
          case PROFILE_TYPES.SUPERVISOR:
            return import('@/store/supervisors/index.js');
          default:
            return null;
        }
      })();
      if (!profile_store) return;
      const module_name = `${this.current_profile.type.toLowerCase()}s`;
      await this.$store.registerModule(module_name, profile_store.default);
      this.$store.dispatch('ASSIGN_CURRENT_PROFILE', { profile: this.current_profile });
    },

    initPWA() {
      if (this.$flipper.enabled(this.$FT.MOBILE_APP_CUSTOMER)) {
        const settings = {
          name: this.current_tenant.attributes.name,
          icon512: this.current_tenant.attributes.picture_logo_url_pwa,
          themeColor: this.current_tenant.attributes.theme_primary_color,
          buttonColor: this.current_tenant.attributes.theme_primary_color,
        };
        settings.uid = window.location.hostname;
        window.progressierAppRuntimeSettings = settings;
        const script = document.createElement('script');
        script.src = 'https://progressier.app/mmTtP8WpP8clXzlTCZSV/script.js';
        script.defer = true;
        document.querySelector('head').appendChild(script);
      }
    },

    initFavicon(picture_favicon_url) {
      // fallback to default favicon if none given or not in production env (used as environment indicator for safety)
      let value = picture_favicon_url;
      if (!value || import.meta.env.VITE_ENV !== VITE_ENV_PRODUCTION) value = TENANT_PICTURE_FAVICON_URL;
      const linkNew = document.createElement('link');
      const linkOld = document.getElementById('dynamic-favicon');
      linkNew.id = 'dynamic-favicon';
      linkNew.rel = 'shortcut icon';
      linkNew.href = value;
      if (linkOld) document.head.removeChild(linkOld);
      document.head.appendChild(linkNew);
    },

    initTitle() {
      if (this.$route.name.includes('supervisor')) {
        document.title = 'Supervisor';
        return;
      }
      let title = '';
      const title_old = document.title;
      // append notifications counter from old title
      if (title_old.startsWith('(')) {
        const notifications_counter = title_old.split(' ')[0];
        title += `${notifications_counter} `;
      }
      // append route name
      const route_name_i18n = this.$t(`window.${this.$route.name}`);
      if (!route_name_i18n.endsWith(this.$route.name)) {
        title += ` ${route_name_i18n}`;
      }
      // append current_tenant name
      if (this.current_tenant) {
        title += ` - ${this.current_tenant.attributes.name}`;
      } else if (this.current_profile && this.current_profile.type === PROFILE_TYPES.SUPERVISOR) {
        title += 'Ziggu';
      }
      document.title = title;
    },

    // TODO: Remove after 2025-02-20 when all users have logged in at least once
    runTicketDraftMigration() {
      if (!this.current_profile) return;
      // Only employees and partners have signatures
      if (![PROFILE_TYPES.EMPLOYEE, PROFILE_TYPES.PARTNER].includes(this.current_profile.type)) return;

      const signature = this.current_profile.attributes?.signature_html;
      runTicketDraftSignatureMigration(signature);
    },
  },
};
</script>
